import { useRef } from 'react';
import styled from 'styled-components';

import { BUTTON_SIZES, BUTTON_VARIANTS, ChatButton, IconButton } from 'ui/button';
import { useDrawerContext } from 'ui/fullScreen';
import { Exit24, Hamburger, LongArrowLeft } from 'ui/icons';
import { QuickNav } from 'modules/quickNav';
import { SEGMENTED_CONTROL_VARIANTS } from 'ui/segmentedControl';
import { colors, smallShadow } from 'ui/theme';
import { useNavBarHeight } from 'utils/hooks/useNavBarHeight';

import { useEditSavedRowDesignContext } from '../context/EditSavedRowDesignContext';
import { useQueryParamContext } from '../context/QueryParamContext';
import { useOnRequestCloseSavedRowDesign } from '../hooks/useOnRequestCloseSavedRowDesign';
import { ViewSwitchToggle } from './Controls';
import { ContentNameInput } from './Controls/ContentNameInput';
import { TemplateEditViews } from './types';

// header for Content editor

const HeaderContainer = styled.div<{ navBarHeight: number }>`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.brandColors.brandPurple};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  height: ${({ navBarHeight }) => `${navBarHeight}px`};
  padding: 0 ${({ theme }) => theme.spacing.values.threeQuarter};
  ${smallShadow};
  > * + * {
    margin-left: ${({ theme }) => theme.spacing.values.threeQuarter};
  }
`;

const TitleContainer = styled.div`
  flex: 1 1 auto;
  margin-left: ${({ theme }) => theme.spacing.utils.pxToRem(20)};
`;

const ControlContainer = styled.div`
  column-gap: ${({ theme }) => theme.spacing.values.threeEights};
  display: flex;
  flex: 0 0 auto;
`;

const StyledQuickNav = styled(QuickNav)<{ navBarHeight: number }>`
  left: 0;
  margin-left: 0;
  top: ${({ navBarHeight }) => `${navBarHeight}px`};
  bottom: 0;
`;

export const ContentHeader = () => {
  const { setView } = useQueryParamContext();
  const { isNavOpen, toggleNav } = useDrawerContext();
  const navBarHeight = useNavBarHeight();
  const navTrigger = useRef(null);

  return (
    <HeaderContainer navBarHeight={navBarHeight}>
      <IconButton
        onClick={() => toggleNav()}
        ref={navTrigger}
        variant={BUTTON_VARIANTS.GHOSTINVERTED}
      >
        <Hamburger color={colors.white} />
      </IconButton>
      <IconButton
        onClick={() => setView(TemplateEditViews.settings)}
        variant={BUTTON_VARIANTS.GHOSTINVERTED}
      >
        <LongArrowLeft color={colors.white} />
      </IconButton>
      <TitleContainer>
        <ContentNameInput isCompact isInverted />
      </TitleContainer>
      <ChatButton size={BUTTON_SIZES.LARGE} variant={BUTTON_VARIANTS.GHOSTINVERTED} />
      <ControlContainer>
        <ViewSwitchToggle variant={SEGMENTED_CONTROL_VARIANTS.TRANSLUCENT} />
      </ControlContainer>
      <StyledQuickNav
        isVisible={isNavOpen}
        triggerEl={navTrigger}
        toggleNav={toggleNav}
        navBarHeight={navBarHeight}
      />
    </HeaderContainer>
  );
};

export const EditSavedRowDesignContentHeader = () => {
  const navBarHeight = useNavBarHeight();
  const onRequestCloseSavedRowDesignEditor = useOnRequestCloseSavedRowDesign();
  const { savedRow } = useEditSavedRowDesignContext();

  return (
    <HeaderContainer navBarHeight={navBarHeight}>
      <IconButton
        onClick={onRequestCloseSavedRowDesignEditor}
        variant={BUTTON_VARIANTS.GHOSTINVERTED}
      >
        <Exit24 color={colors.white} />
      </IconButton>

      <TitleContainer>
        <ContentNameInput isCompact isInverted savedRowName={savedRow?.name} />
      </TitleContainer>

      <ChatButton size={BUTTON_SIZES.LARGE} variant={BUTTON_VARIANTS.GHOSTINVERTED} />
    </HeaderContainer>
  );
};
